import React, { useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useFetchAssetStorageLimit } from "src/query/settings/dataSource";
import { StorageTrackerType } from "src/types/settings/dataSource";

import _ from "lodash";

import styles from "./styles.module.scss";

const StorageTracker: React.FC = () => {

  const progressBarRef = useRef<HTMLDivElement>(null);
  const { data } = useFetchAssetStorageLimit();
  const [selected, setSelected] = useState(StorageTrackerType.PAPERFLITE);

  if (!data) return null;

  const handleSelect = (value: StorageTrackerType) => {
    setSelected(value);
  };

  const selectedData =
    selected === StorageTrackerType.PAPERFLITE
      ? {
        used: data.assetStorageUsed,
        total: data.assetStorageLimit,
        percent: Math.round((data.assetStorageUsed / data.assetStorageLimit) * 100),
      }
      : {
        used: data.datasourceFilesUsed,
        total: data.datasourceFilesLimit,
        percent: Math.round((data.datasourceFilesUsed / data.datasourceFilesLimit) * 100),
      };

  // Calculate the percentage of storage used based on the selected storage type    
  const percent = Math.round((selectedData.used / selectedData.total) * 100);

  const totalSegments = 70;
  const segmentGap = 1;
  const filledSegmentCount = Math.round((selectedData.percent / 100) * totalSegments);

  // Initialize the horizontal position (in pixels) for the indicator

  let indicatorPositionPx = 0;

  if (progressBarRef.current) {
    // Calculate the position of the indicator:
    // 1. Width of one segment = total width of progress bar / totalSegments
    // 2. Multiply by the number of filled segments to get the indicator position
    // 3. Add half of the total gap for the filled segments to adjust for spacing
    indicatorPositionPx =
      filledSegmentCount * (progressBarRef.current.offsetWidth / totalSegments) +
      (filledSegmentCount * segmentGap) / 2;
  }

  return (
    <div
      className={styles.storageTracker}
    >
      <div className={styles.header}>
        <div className={styles.title}>Storage Limit</div>

        <Dropdown>
          <Dropdown.Toggle as="div" id="dropdown-autoclose-true" className={styles.dropDown}>
            {selected} <i className="paperflite-dropdown" />
          </Dropdown.Toggle>

          <Dropdown.Menu className={styles.customMenu}>
            <Dropdown.Item onClick={() => handleSelect(StorageTrackerType.PAPERFLITE)}>
              <i
                className={
                  selected === StorageTrackerType.PAPERFLITE
                    ? "paperflite-radio-button-selected"
                    : "paperflite-radio-button-unselected"
                }
              />{" "}
              <span className={styles.label}>Paperflite</span>
            </Dropdown.Item>

            <Dropdown.Item onClick={() => handleSelect(StorageTrackerType.CONTENT_SOURCE)}>
              <i
                className={
                  selected === StorageTrackerType.CONTENT_SOURCE
                    ? "paperflite-radio-button-selected"
                    : "paperflite-radio-button-unselected"
                }
              />{" "}
              <span className={styles.label}>Content Source</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>


      <div className={styles.progressWrapper}>
        <div className={styles.progressBar} ref={progressBarRef}>
          {_.times(70, (index) => (
            <div
              key={index}
              className={`${styles.progressSegment} ${index < Math.round((selectedData.percent / 100) * 70)
                ? styles.progressSegmentFilled
                : ""
                }`}
            />
          ))}
        </div>
        <div
          className={styles.progressPercent}
          style={{
            left: `${indicatorPositionPx}px`,
            transform: "translateX(-50%)",
          }}
        >
          {selectedData.percent}%
        </div>
        <div className={styles.usage}>
          {selected === StorageTrackerType.PAPERFLITE && (
            <>
              <span className={styles.used}>{selectedData.used.toFixed(2)}GB</span> / {selectedData.total}GB used
            </>
          )}

          {selected === StorageTrackerType.CONTENT_SOURCE && (
            <>
              <span className={styles.used}>{selectedData.used}</span> / {selectedData.total} assets used
            </>
          )}
        </div>
      </div>
      <div className={styles.detailsWrapper}>
        <div className={styles.details}>
          {selected === StorageTrackerType.PAPERFLITE ? (
            <>
              <div className={styles.detailRow}>
                <span className={styles.consumedDot}></span>
                <span key={`${selected}-consumption`} className={styles.slideInRight}>
                  Total consumption ({percent}%)
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.remainingDot}></span>
                <span key={`${selected}-remaining`} className={styles.slideInRight}>
                  Remaining limit ({100 - percent}%)
                </span>
              </div>
            </>
          ) : (
            <>
              <div className={styles.detailRow}>
                <span className={styles.consumedDot}></span>
                <span key={`${selected}-uploaded`} className={styles.slideInRight}>
                  Total assets uploaded ({selectedData.used})
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.remainingDot}></span>
                <span key={`${selected}-assets`} className={styles.slideInRight}>
                  Remaining assets ({selectedData.total - selectedData.used})
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageTracker;
